import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { config } from './config'
import { useAuth, Loading, logout as apiLogout } from './auth'

// ── Types ────────────────────────────────────────────────────────────────────

type ModelStat = {
  model_group: string
  model_description: string
  codes: string[]
  order_count: number
  bto_count: number
  bto_ratio: number
  low_bto: boolean
}

type AdminStats = {
  tables: Record<string, number>
  model_groups: number
  models: ModelStat[]
}

type SignedInAdmin = {
  email: string
  name: string | null
  first_login: string
  last_login: string
  login_count: number
}

type AdminAccessData = {
  admin_domain: string | null
  admin_emails: string[]
  user_login_username: string
  signed_in_admins: SignedInAdmin[]
  note: string
}

type ActivityEvent = {
  id: number
  created_at: string
  actor_role: string
  actor_name: string | null
  action: string
  model_codes: string[]
  n_cars: number | null
  detail: Record<string, unknown> | null
}

function fmtTime(iso: string): string {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleString()
}

// ── Shell ──────────────────────────────────────────────────────────────────────

function NavItem({ to, end, children }: { to: string; end?: boolean; children: ReactNode }) {
  return (
    <NavLink
      to={to} end={end}
      className={({ isActive }) =>
        `block border-l-2 px-3 py-1.5 text-[13px] font-bold uppercase tracking-[0.06em] ${
          isActive ? 'border-bmw-ocean text-black' : 'border-transparent text-[#666] hover:text-black'
        }`}
    >
      {children}
    </NavLink>
  )
}

export function AdminConsole() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const doLogout = async () => {
    await apiLogout()
    navigate('/login', { replace: true })
  }
  return (
    <div className="flex min-h-dvh bg-white">
      <aside className="flex w-60 flex-shrink-0 flex-col border-r border-[#E5E7EC] bg-[#FAFBFC]">
        <div className="flex h-14 items-center gap-2.5 border-b border-[#E5E7EC] px-5">
          <img src="/bmw-mini-lockup-grey.png" alt="BMW + MINI" className="h-6" />
          <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-black">Admin</span>
        </div>
        <nav className="flex-1 space-y-0.5 px-3 py-4">
          <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#999]">Overview</div>
          <NavItem to="/admin" end>Data &amp; models</NavItem>
          <NavItem to="/admin/model">Model explorer</NavItem>
          <NavItem to="/admin/tree">Chain tree</NavItem>
          <NavItem to="/admin/benchmark">Human benchmark</NavItem>
          <NavItem to="/admin/validation">Data benchmark</NavItem>
          <NavItem to="/admin/activity">Activity</NavItem>
          <NavItem to="/admin/access">Access</NavItem>
          <div className="mt-4 border-t border-[#E5E7EC] pt-3">
            <a href="/" className="block px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] text-bmw-ocean hover:underline">
              ← Configurator
            </a>
          </div>
        </nav>
        <div className="border-t border-[#E5E7EC] p-3">
          <div className="mb-2 truncate text-xs text-[#666]">{user?.name || user?.email || 'admin'}</div>
          <button
            type="button" onClick={doLogout}
            className="w-full border border-[#CFD3DB] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-black hover:text-black"
          >
            Log out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 overflow-x-auto px-8 py-8">
        <Outlet />
      </main>
    </div>
  )
}

// ── Overview (data & models health) ─────────────────────────────────────────────

const TABLE_LABELS: Record<string, string> = {
  orders: 'Orders',
  packages: 'Packages',
  package_contents: 'Package contents',
  rim_codes: 'Rims',
  constraints: 'Constraints',
  option_descriptions: 'Options',
  nmc_model_codes: 'NMC codes',
  nmc_edition_mapping: 'Edition maps',
}

export function AdminOverview() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    fetch(`${config.apiUrl}/api/admin/stats`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setStats)
      .catch(() => setErr('Could not load stats'))
  }, [])
  if (err) return <p className="text-sm text-black">⚠ {err}</p>
  if (!stats) return <Loading />

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-[15px] font-bold uppercase tracking-[0.08em] text-black">Data &amp; models</h1>
        <p className="mt-1 text-xs text-[#999]">Reference data currently loaded in the database.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Object.entries(stats.tables ?? {}).map(([k, v]) => (
          <div key={k} className="border border-[#E5E7EC] p-4" style={{ borderTop: '2px solid #92A2BD' }}>
            <div className="text-2xl font-bold tabular-nums text-black">{v.toLocaleString()}</div>
            <div className="mt-0.5 text-[11px] uppercase tracking-[0.06em] text-[#999]">{TABLE_LABELS[k] ?? k}</div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-[#444]">
          Model catalog · {stats.model_groups ?? 0} models
        </h2>
        <div className="overflow-x-auto border border-[#E5E7EC]">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-bmw-ocean bg-[#FAFBFC] text-[10px] font-bold uppercase tracking-[0.12em] text-bmw-ocean">
                <th className="px-4 py-2.5 text-left">Model</th>
                <th className="px-4 py-2.5 text-left">Group</th>
                <th className="px-4 py-2.5 text-left">Codes</th>
                <th className="px-4 py-2.5 text-right">Orders</th>
                <th className="px-4 py-2.5 text-right">BTO</th>
                <th className="px-4 py-2.5 text-right">BTO ratio</th>
              </tr>
            </thead>
            <tbody>
              {(stats.models ?? []).map(m => (
                <tr key={`${m.model_group}|${m.model_description}`} className="border-b border-[#E5E7EC] last:border-b-0 hover:bg-[#FAFBFC]">
                  <td className="px-4 py-2.5 font-bold text-black">{m.model_description}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#666]">{m.model_group}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#666]">{m.codes.join(' + ')}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{m.order_count.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[#666]">{m.bto_count.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    <span className={m.low_bto ? 'font-bold text-black' : 'text-[#666]'}>{(m.bto_ratio * 100).toFixed(0)}%</span>
                    {m.low_bto && (
                      <span className="ml-2 border border-black px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-black">low BTO</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── Access (read-only) ──────────────────────────────────────────────────────────

function Field({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[#E5E7EC] py-2.5 last:border-b-0">
      <span className="text-xs uppercase tracking-[0.06em] text-[#999]">{label}</span>
      <span className={`text-sm ${muted ? 'italic text-[#999]' : 'text-black'}`}>{value}</span>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border border-[#E5E7EC]" style={{ borderTop: '2px solid #92A2BD' }}>
      <div className="border-b border-[#E5E7EC] bg-[#FAFBFC] px-4 py-2.5 text-xs font-bold uppercase tracking-[0.08em] text-[#444]">{title}</div>
      <div className="px-4">{children}</div>
    </div>
  )
}

export function AdminAccess() {
  const [data, setData] = useState<AdminAccessData | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    fetch(`${config.apiUrl}/api/admin/access`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setErr('Could not load access info'))
  }, [])
  if (err) return <p className="text-sm text-black">⚠ {err}</p>
  if (!data) return <Loading />

  // Defensive against an API that predates these fields.
  const emails = data.admin_emails ?? []
  const admins = data.signed_in_admins ?? []

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="text-[15px] font-bold uppercase tracking-[0.08em] text-black">Access</h1>
        <p className="mt-1 text-xs text-[#999]">Who can sign in. Passwords and secrets are managed in Secret Manager.</p>
      </div>

      <Panel title="Admins · Google SSO">
        {data.admin_domain && <Field label="Domain" value={`@${data.admin_domain}`} />}
        <Field label="Allowlisted emails" value={emails.length ? emails.join(', ') : '—'} />
      </Panel>

      <div>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-[#444]">
          Signed-in admins · {admins.length}
        </h2>
        {admins.length === 0 ? (
          <p className="text-xs text-[#999]">No admin has signed in yet.</p>
        ) : (
          <div className="overflow-x-auto border border-[#E5E7EC]">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-bmw-ocean bg-[#FAFBFC] text-[10px] font-bold uppercase tracking-[0.12em] text-bmw-ocean">
                  <th className="px-4 py-2.5 text-left">Email</th>
                  <th className="px-4 py-2.5 text-left">Name</th>
                  <th className="px-4 py-2.5 text-left">Last sign-in</th>
                  <th className="px-4 py-2.5 text-right">Logins</th>
                </tr>
              </thead>
              <tbody>
                {admins.map(a => (
                  <tr key={a.email} className="border-b border-[#E5E7EC] last:border-b-0 hover:bg-[#FAFBFC]">
                    <td className="px-4 py-2.5 text-black">{a.email}</td>
                    <td className="px-4 py-2.5 text-[#666]">{a.name || '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums text-[#666]">{fmtTime(a.last_login)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{a.login_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Panel title="Normal users · shared login">
        <Field label="Username" value={data.user_login_username} />
        <Field label="Password" value="Managed in Secret Manager" muted />
      </Panel>

      <p className="text-[11px] text-[#999]">{data.note}</p>
    </div>
  )
}

// ── Activity (user/admin action trail, grouped by editing session) ────────────

type ActivityGroup = {
  key: string
  actor_name: string | null
  actor_role: string
  model_codes: string[]
  events: ActivityEvent[] // newest first
}

function detailStr(e: ActivityEvent, k: string): string | null {
  const v = e.detail?.[k]
  return typeof v === 'string' ? v : null
}

function eventSummary(e: ActivityEvent): string {
  const s = detailStr(e, 'summary')
  if (s) return s
  if (e.action === 'predict') return `Generated a prediction (${e.n_cars ?? '?'} cars)`
  if (e.action === 'export') return `Exported (${e.n_cars ?? '?'} cars)`
  return e.action
}

export function AdminActivity() {
  const [events, setEvents] = useState<ActivityEvent[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [open, setOpen] = useState<Set<string>>(new Set())
  useEffect(() => {
    fetch(`${config.apiUrl}/api/admin/activity?limit=300`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => setEvents(d.events ?? []))
      .catch(() => setErr('Could not load activity'))
  }, [])
  if (err) return <p className="text-sm text-black">⚠ {err}</p>
  if (!events) return <Loading />

  // Group by actor + editing session + model. Events without a session id each
  // stand alone. Fetch order is newest-first, preserved here.
  const groups: ActivityGroup[] = []
  const byKey = new Map<string, ActivityGroup>()
  for (const e of events) {
    const sid = detailStr(e, 'session_id')
    const key = `${e.actor_name ?? '?'}::${sid ?? `#${e.id}`}::${e.model_codes.join(',')}`
    let g = byKey.get(key)
    if (!g) {
      g = { key, actor_name: e.actor_name, actor_role: e.actor_role, model_codes: e.model_codes, events: [] }
      byKey.set(key, g)
      groups.push(g)
    }
    g.events.push(e)
  }

  const toggle = (k: string) =>
    setOpen(prev => {
      const n = new Set(prev)
      if (n.has(k)) n.delete(k); else n.add(k)
      return n
    })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[15px] font-bold uppercase tracking-[0.08em] text-black">Activity</h1>
        <p className="mt-1 text-xs text-[#999]">
          Each row is one editing session — generate, modifications, export — on a model.
          Click to expand. Normal users share one login, so actions show as the shared user.
        </p>
      </div>

      {groups.length === 0 ? (
        <p className="text-xs text-[#999]">No activity recorded yet.</p>
      ) : (
        <div className="divide-y divide-[#E5E7EC] border border-[#E5E7EC]">
          {groups.map(g => {
            const counts: Record<string, number> = {}
            for (const e of g.events) counts[e.action] = (counts[e.action] ?? 0) + 1
            const parts: string[] = []
            if (counts.predict) parts.push(`${counts.predict} generated`)
            if (counts.modify) parts.push(`${counts.modify} ${counts.modify === 1 ? 'modification' : 'modifications'}`)
            if (counts.export) parts.push(`${counts.export} exported`)
            const isOpen = open.has(g.key)
            return (
              <div key={g.key}>
                <button
                  type="button" onClick={() => toggle(g.key)}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-[#FAFBFC]"
                >
                  <span className="w-3 flex-shrink-0 text-[#999]">{isOpen ? '▾' : '▸'}</span>
                  <span className="w-40 flex-shrink-0 tabular-nums text-xs text-[#999]">{fmtTime(g.events[0].created_at)}</span>
                  <span className="text-sm text-black">
                    {g.actor_name || '—'}
                    {g.actor_role === 'admin' && (
                      <span className="ml-1.5 text-[9px] font-bold uppercase tracking-[0.06em] text-bmw-ocean">admin</span>
                    )}
                  </span>
                  <span className="font-mono text-xs text-[#666]">{g.model_codes.join(' + ') || '—'}</span>
                  <span className="ml-auto text-xs text-[#666]">{parts.join(' · ')}</span>
                </button>
                {isOpen && (
                  <div className="bg-[#FAFBFC] px-4 pb-3 pt-1">
                    <ol className="space-y-1.5 border-l border-[#CFD3DB] pl-4">
                      {[...g.events].reverse().map(e => (
                        <li key={e.id} className="flex flex-wrap gap-x-3 text-xs">
                          <span className="w-40 flex-shrink-0 tabular-nums text-[#999]">{fmtTime(e.created_at)}</span>
                          <span className="text-[#333]">{eventSummary(e)}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
